import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";

// Public origin for canonical URLs. At the domain cutover set REACT_APP_SITE_URL
// (e.g. https://hindisahitya.in) so pages served from other hosts point there.
export const siteUrl = () => (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");

const clip = (text, n = 158) => {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  return s.length <= n ? s : s.slice(0, s.lastIndexOf(" ", n - 1) > 80 ? s.lastIndexOf(" ", n - 1) : n - 1) + "…";
};

function setTag(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!attrs) { el?.remove(); return; }
  if (!el) { el = create(); document.head.appendChild(el); }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
}
const meta = (name, content, prop = "name") =>
  setTag(`meta[${prop}="${name}"]`, () => { const m = document.createElement("meta"); m.setAttribute(prop, name); return m; },
    content ? { content } : null);

/**
 * Per-page <title>, description, canonical, robots and Open Graph tags. One
 * copy of each tag is kept in <head> and updated on every page, so tags never
 * pile up during SPA navigation. `canonical` defaults to the path without its
 * query string; pass a path (e.g. with ?page=2) where the query matters.
 */
export function usePageMeta({ title, description, noindex = false, canonical, type = "website", image } = {}) {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  useEffect(() => {
    const site = t("site_name");
    const full = title ? `${title} · ${site}` : site;
    const url = siteUrl() + (canonical ?? pathname);
    const desc = clip(description || t("hero_subtitle"));
    document.title = full;
    meta("description", desc);
    meta("robots", noindex ? "noindex, follow" : null);
    setTag('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; },
      noindex ? null : { href: url });
    meta("og:title", full, "property");
    meta("og:description", desc, "property");
    meta("og:url", url, "property");
    meta("og:type", type, "property");
    meta("og:site_name", site, "property");
    meta("og:locale", lang === "hi" ? "hi_IN" : "en_IN", "property");
    meta("og:image", image || null, "property");
  }, [title, description, noindex, canonical, type, image, pathname, t, lang]);
}

/** schema.org BreadcrumbList for [{name, path}] items. */
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: siteUrl() + it.path })),
});
